import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react'
import { adminApi, type AdminFeedbackItem, type FeedbackInboxStatus } from '@/api/admin'
import { Card } from '@/components/ui/Card'
import { PageHeader } from '@/components/PageHeader'
import { copy } from '@/lib/copy'
import { getApiErrorMessage } from '@/lib/apiError'
import { cn } from '@/components/ui/cn'

const FEEDBACK_QUERY_KEY = ['admin-feedback'] as const
const UNREAD_COUNT_KEY = ['admin-feedback-unread-count'] as const

const FILTERS: { status: FeedbackInboxStatus; label: string }[] = [
  { status: 'UNREAD', label: copy.admin.feedbackFilterUnread },
  { status: 'ALL', label: copy.admin.feedbackFilterAll },
  { status: 'READ', label: copy.admin.feedbackFilterRead },
]

function FeedbackRow({
  item,
  onToggleRead,
  pending,
}: {
  item: AdminFeedbackItem
  onToggleRead: (id: string, read: boolean) => void
  pending: boolean
}) {
  return (
    <Card className={cn('p-4', !item.read && 'border-primary/30 bg-primary/[0.03]')}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            {!item.read ? (
              <span className="h-2 w-2 shrink-0 rounded-full bg-primary" aria-hidden />
            ) : null}
            <span className="rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
              {item.category}
            </span>
            <span className="text-xs text-muted-foreground">
              {new Date(item.createdAt).toLocaleString()}
            </span>
            {item.pagePath ? (
              <span className="truncate text-xs text-muted-foreground">{item.pagePath}</span>
            ) : null}
          </div>
          <p className={cn('mb-2 text-sm', item.read ? 'font-medium' : 'font-semibold', 'text-foreground')}>
            {item.email ?? copy.admin.feedbackAnonymous}
          </p>
          <p className="whitespace-pre-wrap text-sm text-foreground/90">{item.message}</p>
          {item.read && item.readByEmail ? (
            <p className="mt-2 text-xs text-muted-foreground">
              {copy.admin.feedbackReadBy.replace('{email}', item.readByEmail)}
              {item.readAt ? ` · ${new Date(item.readAt).toLocaleString()}` : ''}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          disabled={pending}
          onClick={() => onToggleRead(item.id, !item.read)}
          className="inline-flex h-9 shrink-0 items-center justify-center rounded-md border px-3 text-xs font-medium hover:bg-secondary disabled:opacity-50"
        >
          {item.read ? copy.admin.feedbackMarkUnread : copy.admin.feedbackMarkRead}
        </button>
      </div>
    </Card>
  )
}

export function AdminFeedbackPage() {
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<FeedbackInboxStatus>('UNREAD')
  const [page, setPage] = useState(0)

  const feedbackQuery = useQuery({
    queryKey: [...FEEDBACK_QUERY_KEY, status, page],
    queryFn: () => adminApi.listFeedback({ status, page, size: 20 }),
  })

  const unreadCountQuery = useQuery({
    queryKey: UNREAD_COUNT_KEY,
    queryFn: () => adminApi.feedbackUnreadCount(),
  })

  const readMutation = useMutation({
    mutationFn: ({ id, read }: { id: string; read: boolean }) => adminApi.updateFeedbackRead(id, read),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: FEEDBACK_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY }),
      ])
    },
  })

  const totalPages = feedbackQuery.data?.totalPages ?? 0
  const unreadCount = unreadCountQuery.data ?? 0

  return (
    <div data-testid="admin-feedback">
      <PageHeader title={copy.admin.feedbackTitle} description={copy.admin.feedbackDescription} />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Link
          to="/admin"
          className="inline-flex h-9 items-center gap-1 rounded-md bg-secondary px-3 text-xs text-secondary-foreground hover:bg-secondary/80"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {copy.admin.backToAdmin}
        </Link>

        <div className="inline-flex rounded-md border p-0.5" role="tablist" aria-label="Feedback folder">
          {FILTERS.map((filter) => {
            const selected = status === filter.status
            const badge =
              filter.status === 'UNREAD' && unreadCount > 0 ? ` (${unreadCount})` : ''
            return (
              <button
                key={filter.status}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => {
                  setStatus(filter.status)
                  setPage(0)
                }}
                className={cn(
                  'rounded px-3 py-1.5 text-xs font-medium transition-colors',
                  selected
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {filter.label}
                {badge}
              </button>
            )
          })}
        </div>
      </div>

      {feedbackQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">{copy.admin.feedbackLoading}</p>
      ) : feedbackQuery.isError ? (
        <p className="text-sm text-destructive">
          {getApiErrorMessage(feedbackQuery.error, copy.admin.feedbackLoadFailed)}
        </p>
      ) : (
        <>
          {readMutation.isError ? (
            <p className="mb-3 text-sm text-destructive">
              {getApiErrorMessage(readMutation.error, copy.admin.feedbackUpdateFailed)}
            </p>
          ) : null}

          <div className="space-y-3">
            {(feedbackQuery.data?.content ?? []).map((item) => (
              <FeedbackRow
                key={item.id}
                item={item}
                pending={readMutation.isPending}
                onToggleRead={(id, read) => readMutation.mutate({ id, read })}
              />
            ))}
            {(feedbackQuery.data?.content.length ?? 0) === 0 ? (
              <p className="text-sm text-muted-foreground">{copy.admin.feedbackEmpty}</p>
            ) : null}
          </div>

          {totalPages > 1 ? (
            <div className="mt-6 flex items-center justify-center gap-3">
              <button
                type="button"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
                className="inline-flex h-9 items-center gap-1 rounded-md border px-3 text-xs disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4" />
                {copy.admin.usersPrev}
              </button>
              <span className="text-sm text-muted-foreground">
                {page + 1} / {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
                className="inline-flex h-9 items-center gap-1 rounded-md border px-3 text-xs disabled:opacity-50"
              >
                {copy.admin.usersNext}
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}
